import { useEffect } from "react";
import { useStoryBibleStore } from "../../../stores/storyBibleStore";
import { useStickyTourStore } from "../../../stores/stickyTourStore";
import { TourSignal } from "../../StickyTour/TourTypes";

function isScrolledToBottom(el, threshold = 1) {
  return el.scrollHeight - el.scrollTop - el.clientHeight < threshold;
}

/**
 * Tracks which Story Bible section is in view for left-nav highlighting.
 * Kept separate from StoryBible so sectionInView updates do not re-render the full panel.
 */
export default function StoryBibleSectionInViewTracker({ centerColumnRef }) {
  const isActive = useStoryBibleStore((state) => state.isActive);
  const sendFlowSignal = useStickyTourStore((state) => state.sendFlowSignal);

  useEffect(() => {
    if (!isActive) {
      useStoryBibleStore.getState().setSectionInView(null);
    }
  }, [isActive]);

  useEffect(() => {
    const centerColumn = centerColumnRef?.current;
    if (!centerColumn || !isActive) return;

    const { elementRefs } = useStoryBibleStore.getState();
    const keys = Object.keys(elementRefs);
    const lastKey = keys[keys.length - 1];
    const lastElementBuffer = 20;

    const handleScroll = () => {
      let newSectionInView = null;
      const scrollContainer = centerColumnRef?.current;
      if (!scrollContainer) return;

      const containerTop = scrollContainer.getBoundingClientRect().top;
      const passedThreshold = {};
      const refs = useStoryBibleStore.getState().elementRefs;

      for (const key in refs) {
        if (!refs[key]?.current) continue;
        const itemTop = refs[key].current.getBoundingClientRect().top;
        if (itemTop <= containerTop + 1) {
          passedThreshold[key] = itemTop;
        }
      }

      if (Object.keys(passedThreshold).length === 0) {
        newSectionInView = null;
      } else {
        newSectionInView = Object.entries(passedThreshold).reduce((a, b) => (a[1] > b[1] ? a : b))[0];
      }

      if (isScrolledToBottom(scrollContainer, lastElementBuffer)) {
        newSectionInView = lastKey;
      }

      const store = useStoryBibleStore.getState();
      const prevSectionInView = store.sectionInView;
      if (prevSectionInView !== newSectionInView) {
        store.setSectionInView(newSectionInView);
        if (prevSectionInView === null && newSectionInView !== null) {
          sendFlowSignal(TourSignal.STORY_BIBLE_SCROLLED_INTO_VIEW);
        }
      }

      const newIsInView = !!newSectionInView;
      if (store.isInView !== newIsInView) {
        store.setIsInView(newIsInView);
      }
    };

    centerColumn.addEventListener("scroll", handleScroll, { passive: true });
    return () => centerColumn.removeEventListener("scroll", handleScroll);
  }, [centerColumnRef, isActive, sendFlowSignal]);

  return null;
}
